import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles,
    Radar, CircleDot
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ITEMS_PERFECCIONISMO = [
    { id: 'estandares', label: 'Mis estándares son muy altos', description: 'Me exijo mucho más de lo necesario' },
    { id: 'errores', label: 'Los errores me afectan mucho', description: 'Un error pequeño puede arruinar mi día' },
    { id: 'tiempo', label: 'Necesito más tiempo del normal', description: 'Reviso y ajusto repetidamente' },
    { id: 'inicio', label: 'Me cuesta empezar', description: 'Espero sentirme "listo" para comenzar' },
    { id: 'critica', label: 'Soy muy crítico conmigo', description: 'Mi voz interna es dura' },
    { id: 'comparacion', label: 'Me comparo con otros', description: 'Mido mi valor contra los demás' }
];

export default function Modulo22RadarExigencia() {
    const [ratings, setRatings] = useState(
        ITEMS_PERFECCIONISMO.reduce((acc, item) => ({ ...acc, [item.id]: [5] }), {})
    );
    const [reflexion1, setReflexion1] = useState('');
    const [reflexion2, setReflexion2] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/radar_exigencia`, {
                    withCredentials: true
                });
                if (data.data) {
                    if (data.data.ratings) {
                        const loadedRatings = {};
                        Object.keys(data.data.ratings).forEach(key => {
                            loadedRatings[key] = [data.data.ratings[key]];
                        });
                        setRatings(loadedRatings);
                    }
                    if (data.data.reflexion1) setReflexion1(data.data.reflexion1);
                    if (data.data.reflexion2) setReflexion2(data.data.reflexion2);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            const ratingsFlat = {};
            Object.keys(ratings).forEach(key => {
                ratingsFlat[key] = ratings[key][0];
            });
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'radar_exigencia', data: { ratings: ratingsFlat, reflexion1, reflexion2 } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [ratings, reflexion1, reflexion2]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [ratings, reflexion1, reflexion2, saveDraft, loadingDraft, readOnly]);

    const promedio = Object.values(ratings).reduce((sum, val) => sum + val[0], 0) / ITEMS_PERFECCIONISMO.length;
    const tipo = promedio >= 7 ? 'defensiva' : promedio >= 4 ? 'moderada' : 'funcional';

    const canSubmit = !readOnly && (reflexion1.trim().length > 0 && reflexion2.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa ambas reflexiones');
            return;
        }

        setLoading(true);
        try {
            const ratingsFlat = {};
            Object.keys(ratings).forEach(key => {
                ratingsFlat[key] = ratings[key][0];
            });
            
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/radar-exigencia/submit`,
                { 
                    ratings: ratingsFlat,
                    reflexion_intencion: reflexion1,
                    reflexion_accion: reflexion2,
                    promedio_calculado: promedio,
                    tipo_exigencia: tipo
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setResultado({ promedio, tipo });
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar');
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        const tipoColors = {
            funcional: { bg: 'bg-[#5FAF9D]', text: 'Funcional' },
            moderada: { bg: 'bg-[#E8B27A]', text: 'Moderada' },
            defensiva: { bg: 'bg-[#D07F7E]', text: 'Defensiva' }
        };
        
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className={`w-20 h-20 ${tipoColors[resultado.tipo].bg} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                            <Radar className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Radar completado</h2>
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <div className="text-center">
                                <p className="text-4xl font-bold text-[#1F2A30]">{resultado.promedio.toFixed(1)}</p>
                                <p className="text-xs text-[#7A8A93] uppercase">Promedio</p>
                            </div>
                            <div className={`px-4 py-2 rounded-full ${tipoColors[resultado.tipo].bg} bg-opacity-20`}>
                                <span className={`font-medium ${tipoColors[resultado.tipo].bg.replace('bg-', 'text-')}`}>
                                    Exigencia {tipoColors[resultado.tipo].text}
                                </span>
                            </div>
                        </div>
                        
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="mb-6"><ModuleFeedback moduleId="radar_exigencia" chapter={2} /></div>
                        <Link to="/usuario">
                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 2</span>
                                    <span className="text-xs text-[#7A8A93]">•</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 2.2</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Radar de exigencia defensiva</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                </span>
                            )}
                            <div className="text-right">
                                <p className="text-2xl font-bold text-[#1F2A30]">{promedio.toFixed(1)}</p>
                                <p className="text-xs text-[#7A8A93]">promedio</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-8">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <Radar className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">¿Tu perfeccionismo te ayuda o te frena?</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Evalúa cada afirmación de 0 a 10</p>
                            </div>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="p-8 space-y-8">
                        {/* Sliders */}
                        <div className="space-y-6">
                            {ITEMS_PERFECCIONISMO.map((item) => (
                                <div key={item.id} className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-[#1F2A30]">{item.label}</p>
                                            <p className="text-xs text-[#7A8A93]">{item.description}</p>
                                        </div>
                                        <span className="text-2xl font-bold text-[#3E5F6E] min-w-[3ch] text-right">
                                            {ratings[item.id][0]}
                                        </span>
                                    </div>
                                    <Slider
                                        value={ratings[item.id]}
                                        onValueChange={(val) => setRatings(prev => ({ ...prev, [item.id]: val }))}
                                        max={10}
                                        min={0}
                                        step={1}
                                        className="w-full"
                                        data-testid={`slider-${item.id}`}
                                    />
                                    <div className="flex justify-between text-xs text-[#7A8A93]">
                                        <span>Nada</span>
                                        <span>Totalmente</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Classification indicator */}
                        <div className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-[#4A5B63]">Clasificación actual:</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    tipo === 'defensiva' ? 'bg-[#D07F7E]/20 text-[#D07F7E]' :
                                    tipo === 'moderada' ? 'bg-[#E8B27A]/20 text-[#E8B27A]' :
                                    'bg-[#5FAF9D]/20 text-[#5FAF9D]'
                                }`}>
                                    Exigencia {tipo === 'defensiva' ? 'Defensiva' : tipo === 'moderada' ? 'Moderada' : 'Funcional'}
                                </span>
                            </div>
                        </div>

                        {/* Reflections */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">
                                    ¿Cuál es tu intención real cuando te exiges tanto?
                                </Label>
                                <Textarea
                                    value={reflexion1}
                                    onChange={(e) => setReflexion1(e.target.value)}
                                    placeholder="¿Qué buscas lograr con esa exigencia?"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="reflexion1-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">
                                    ¿Qué pasa realmente con tu acción cuando te exiges así?
                                </Label>
                                <Textarea
                                    value={reflexion2}
                                    onChange={(e) => setReflexion2(e.target.value)}
                                    placeholder="¿Avanzas más o te paralizas?"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="reflexion2-input"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                                data-testid="submit-radar"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar módulo
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
